<template>
  <article>
    <activity-stage :customRootContainerClass="'face-flip full-height'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <section
        slot="content"
        :style="{ height: '100%', width: isMobile ? 'calc(100% - 77px)' : '100%' }"
      >
        <div class="cards-container">
          <button
            @blur="handleOnPuzzleBlur"
            @focus="handleOnPuzzleFocus"
            @keyup.enter="handleOnPuzzlePlaceholderEnterEvent"
            v-for="(card, index) in currentRound?.puzzlePieceCards ?? []"
            :aria-label="`Droppable area for ${card.label}`"
            :aria-pressed="false"
            :data-placeholder-name="card.label"
            :id="`drop-${index}`"
            :key="`card-${index}`"
            :class="`figure-placeholder figure-type-${currentRound?.puzzlePieceCards?.length}`"
            tabindex="-1"
          >
            <svg :aria-labelledby="`${card.label}_label`" width="98%" height="100%">
              <image :xlink:href="card.letterCard.image.src" width="100%" height="100%"></image>
              <image
                class="mainImg"
                :xlink:href="card.answerImage.image.src"
                width="100%"
                height="100%"
                y="28%"
              ></image>
              <image
                v-if="!isMatched[`m${index}`]"
                :xlink:href="card.puzzlePiece.missing.image.src"
                :width="`${card.puzzlePiece.width}%`"
                height="100%"
                :x="`${card.puzzlePiece.x}%`"
                :y="`${card.puzzlePiece.y}%`"
              ></image>
            </svg>
            <figcaption v-if="caption[`c${index}`]" :id="`${card.label}_label`">
              <b v-text="card.label"></b>
            </figcaption>
          </button>
        </div>
        <div class="puzzles-container">
          <button
            @blur="handleOnPuzzleBlur"
            @focus="handleOnPuzzleFocus"
            @keyup.enter="handleOnPuzzleEnterEvent"
            v-for="card in shuffledSteps"
            :aria-label="`Puzzle piece for ${card.label}`"
            :aria-pressed="false"
            :data-puzzle-name="card.label"
            :id="`drag-${card.defaultIndex}`"
            :key="`puzzle-${card.defaultIndex}`"
            :class="`figure-card figure-type-${shuffledSteps.length} pure-button`"
            :tabindex="0"
          >
            <svg width="98%" height="100%">
              <!-- gets display: none after translating -->
              <mask :id="`mask-${card.defaultIndex}`" :style="maskStyle[card.defaultIndex]">
                <image
                  :xlink:href="card.puzzlePiece.full.image.src"
                  :width="`${card.puzzlePiece.width + 11}%`"
                  height="100%"
                  :x="`${card.puzzlePiece.x - 6.5}%`"
                  :y="`${card.puzzlePiece.y}%`"
                ></image>
              </mask>
              <image
                :xlink:href="card.puzzlePiece.full.image.src"
                :width="`${card.puzzlePiece.width + 11}%`"
                height="100%"
                :x="`${card.puzzlePiece.x - 6.5}%`"
                :y="`${card.puzzlePiece.y}%`"
              ></image>
              <!-- gets display: block after translating -->
              <svg
                width="100%"
                :mask="`url(#mask-${card.defaultIndex})`"
                :style="svgStyle[card.defaultIndex]"
              >
                <image :xlink:href="card.letterCard.image.src" width="100%" height="100%"></image>
                <image
                  :xlink:href="card.answerImage.image.src"
                  width="100%"
                  height="100%"
                  y="28%"
                ></image>
              </svg>
            </svg>
          </button>
        </div>
      </section>
    </activity-stage>
  </article>
</template>

<script>
import _ from 'lodash';
import { TweenMax, TimelineLite, TweenLite, Power0 } from 'gsap';
import Draggable from 'gsap/Draggable';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  components: {
    ActivityStage
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  computed: {
    isPuzzlePiecePressed() {
      return true;
    }
  },

  data() {
    return {
      activityName: 'PuzzlePieces',
      sfx: null,
      roundSetupTime: 0.8,
      maskStyle: [],
      svgStyle: [],
      shuffledSteps: [],
      caption: {
        c0: false,
        c1: false,
        c2: false,
        c3: false,
        c4: false
      },
      isMatched: {
        m0: false,
        m1: false,
        m2: false,
        m3: false,
        m4: false
      }
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  watch: {
    currentRound(next) {
      if (next && Object.keys(next).length) {
        const stepsWithDefaultIndex = next.puzzlePieceCards.map((step, index) => ({
          ...step,
          defaultIndex: index
        }));
        this.shuffledSteps = _.shuffle(stepsWithDefaultIndex);
        if (this.progress.roundIndex) {
          const instructions = `Round ${this.progress.roundIndex +
            1} - Move the pieces to finish each puzzle.`;

          this.setScreenReaderAlert(instructions);
        }
      }
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds({ stepIndicator: 'puzzlePieceCards' });
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(round => {
        round.puzzlePieceCards.forEach(({ letterCard, answerImage }) => {
          this.queueImage(letterCard.image.src);
          this.queueImage(answerImage.image.src);
        });
      });
      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(round => {
        round.puzzlePieceCards.forEach(({ answerAudio }) => {
          this.queueAudio(answerAudio.audio.src);
        });
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(round =>
        round.puzzlePieceCards.map(({ answerAudio }) => cache.get(answerAudio.audio.src))
      );
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        if (!this.currentRound?.puzzlePieceCards) return;

        const { length: stepsCount } = this.currentRound.puzzlePieceCards;
        this.svgStyle = new Array(stepsCount).fill(0).map(() => ({}));
        this.maskStyle = new Array(stepsCount).fill(0).map(() => ({}));
        TweenMax.to('.activity-stage', this.roundSetupTime, { opacity: 1 });
        this.dragAndDrop();
      });
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (!this.isEndOfActivity && !this.isLastRound) {
        timeline.delay(this.roundSetupTime).call(() => {
          this.resetFigureCardDefaultStates();
          this.resetFigureCardPlacHolderDefaultStates();
          this.setupNewRound();
          const figures = Array.from(document.querySelectorAll('.figure-card'));
          figures.forEach(card => TweenMax.set(card, { opacity: 1, transform: 'none' }));

          this.$nextTick(() => {
            const { length: stepsCount } = this.currentRound.puzzlePieceCards;
            this.svgStyle = new Array(stepsCount).fill(0).map(() => ({}));
            this.maskStyle = new Array(stepsCount).fill(0).map(() => ({}));
            this.caption = {
              c0: false,
              c1: false,
              c2: false,
              c3: false,
              c4: false
            };
            this.isMatched = {
              m0: false,
              m1: false,
              m2: false,
              m3: false,
              m4: false
            };
            this.dragAndDrop();
          });
        });
      } else {
        timeline.delay(this.roundSetupTime + 0.5).call(this.completeActivity);
      }
    },

    handleDragEnd(dragContext, componentInstance, drag, drop, index) {
      const self = componentInstance;
      if (dragContext.hitTest(drop, '75%')) {
        const i = Number(dragContext.target.id.split('-')[1]);
        dragContext.applyBounds(drop);
        self.isMatched[`m${i}`] = true;
        self.caption[`c${i}`] = true;
        self.sfx[this.progress.roundIndex][i].play();
        self.sndBell();

        drag.setAttribute(
          'aria-label',
          `Completed ${drag.getAttribute('data-puzzle-name').toLowerCase()} puzzle piece`
        );
        drop.setAttribute(
          'aria-label',
          `Completed ${drag.getAttribute('data-puzzle-name').toLowerCase()} puzzle piece`
        );
        drag.setAttribute('aria-hidden', true);
        drag.setAttribute('tabindex', -1);
        drag.setAttribute('data-complete', '1');
        drop.setAttribute('data-complete', '1');

        this.resetFigureCardAndPlaceholderHighlightStates();

        if (self.checkRoundEnd()) {
          self.handleRoundEnd();
        }
        self.maskStyle = self.maskStyle.map((style, sI) =>
          sI === index ? { display: 'none' } : style
        );
        self.svgStyle = self.svgStyle.map((style, sI) =>
          sI === index ? { display: 'block' } : style
        );
      } else {
        self.sndError();
        TweenLite.to(drag, self.roundSetupTime, {
          x: 0,
          y: 0,
          ease: Power0.easeNone
        });
      }
    },

    dragAndDrop() {
      const self = this;
      const { puzzlePieceCards } = this.currentRound;
      const { isMobile } = this.userDevice;
      const tLite = new TimelineLite();
      let bounds = null;

      puzzlePieceCards.forEach((card, index) => {
        let dragTimer = 0;
        let hasHitBoundary = false;
        const drag = document.getElementById(`drag-${index}`);
        const drop = document.getElementById(`drop-${index}`);

        Draggable.create(drag, {
          autoScroll: 1,
          cursor: 'grab',
          throwProps: true,
          minimumMovement: 0,
          onPress() {
            if (bounds) return;
            bounds = getActivityBoundings();
          },
          onDrag() {
            // work-around for iOS not firing touchend on tablet
            if (!isMobile) return;
            clearTimeout(dragTimer);
            const { pointerX, pointerY } = this;
            const hasHitBoundings = hasTouchedBoundings(pointerX, pointerY, bounds);
            if (hasHitBoundings && !hasHitBoundary) {
              dragTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDragEnd(this, self, drag, drop);
              }, 200);
            }
          },
          onDragStart() {
            tLite.set(drag, { cursor: 'grabbing' });
            self.sndType();
          },
          onDragEnd() {
            this.vars.cursor = 'grab';
            clearTimeout(dragTimer);
            hasHitBoundary = false;
            self.handleDragEnd(this, self, drag, drop, index);
          }
        });
        // Draggable.get(drag).vars.cursor = 'grabbing';
      });
    },

    checkRoundEnd() {
      const maxRounds = this.currentRound.puzzlePieceCards.length;
      const values = Object.values(this.caption);
      for (let i = 0; i < maxRounds; i += 1) {
        if (!values[i]) {
          return false;
        }
      }
      return true;
    },

    handleOnPuzzleEnterEvent(event) {
      const target = event.target;
      const isPuzzleHighlighted = target.classList.contains('highlight');

      Array.from(document.getElementsByClassName('figure-card')).forEach(node => {
        if (node.getAttribute('id') !== target.getAttribute('id')) {
          const isPreviouslyCompleted = +node.getAttribute('data-complete');
          this.resetPreviouslySelectedFigureCardState(
            node,
            !isPreviouslyCompleted && isPuzzleHighlighted
          );
        }
      });

      if (isPuzzleHighlighted) {
        target.classList.remove('highlight');
        targetnode.setAttribute('aria-pressed', false);
      } else {
        target.classList.add('highlight');
        target.setAttribute('aria-pressed', true);
      }

      Array.from(document.getElementsByClassName('figure-placeholder')).forEach(node => {
        const isPreviouslyCompleted = +node.getAttribute('data-complete');

        node.setAttribute('aria-pressed', false);
        node.setAttribute('tabindex', isPreviouslyCompleted || isPuzzleHighlighted ? -1 : 0);

        isPreviouslyCompleted || isPuzzleHighlighted
          ? node.classList.remove('highlight')
          : node.classList.add('highlight');
      });
    },

    handleOnPuzzlePlaceholderEnterEvent(event) {
      const target = event.target;
      let puzzleNode = null;

      Array.from(document.getElementsByClassName('figure-card')).forEach(node => {
        if (node.classList.contains('highlight')) {
          puzzleNode = node;
        }
      });

      if (puzzleNode) {
        const placeholderId = target.getAttribute('id');
        const placeholderName = target.getAttribute('data-placeholder-name');
        const puzzleName = puzzleNode.getAttribute('data-puzzle-name');

        if (placeholderName === puzzleName) {
          target.setAttribute('aria-label', `Completed ${puzzleName.toLowerCase()} puzzle piece`);
          target.setAttribute('aria-pressed', true);
          target.setAttribute('data-complete', '1');

          puzzleNode.setAttribute(
            'aria-label',
            `Completed ${puzzleName.toLowerCase()} puzzle piece`
          );
          puzzleNode.setAttribute('aria-hidden', true);
          puzzleNode.setAttribute('aria-pressed', false);
          puzzleNode.setAttribute('data-complete', '1');
          puzzleNode.classList.remove('highlight');
          puzzleNode.style.visibility = 'hidden';

          const placeholderNodeIndex = Number(placeholderId.split('-')[1]);
          this.handleOnSuccessPerKeyboardPress(placeholderNodeIndex);

          Array.from(document.getElementsByClassName('figure-placeholder')).forEach(node => {
            if (node.getAttribute('id') !== placeholderId) {
              node.setAttribute('aria-pressed', false);
            }
            node.classList.remove('highlight');
            node.setAttribute('tabindex', -1);
          });

          Array.from(document.getElementsByClassName('figure-card')).forEach(node => {
            const isPreviouslyCompleted = +node.getAttribute('data-complete');
            node.setAttribute('tabindex', isPreviouslyCompleted ? -1 : 0);
          });
        } else {
          this.sndError();
        }
      }
    },

    handleOnPuzzleBlur(event) {
      event.target.classList.remove('highlight-in-focus');
    },

    handleOnPuzzleFocus(event) {
      event.target.classList.add('highlight-in-focus');
    },

    handleOnSuccessPerKeyboardPress(placeholderNodeIndex) {
      this.isMatched[`m${placeholderNodeIndex}`] = true;
      this.caption[`c${placeholderNodeIndex}`] = true;
      this.sfx[this.progress.roundIndex][placeholderNodeIndex].play();
      this.sndBell();

      if (this.checkRoundEnd()) {
        this.handleRoundEnd();
      }
    },

    resetFigureCardAndPlaceholderHighlightStates() {
      Array.from(document.getElementsByClassName('figure-card')).forEach(node => {
        const isPreviouslyCompleted = +node.getAttribute('data-complete');

        node.classList.remove('highlight');
        node.setAttribute('aria-pressed', false);
        node.setAttribute('tabindex', isPreviouslyCompleted ? -1 : 0);
      });
      Array.from(document.getElementsByClassName('figure-placeholder')).forEach(node => {
        node.classList.remove('highlight');
        node.setAttribute('aria-pressed', false);
        node.setAttribute('tabindex', -1);
      });
    },

    resetFigureCardDefaultStates() {
      Array.from(document.getElementsByClassName('figure-card')).forEach(node => {
        node.setAttribute('aria-hidden', false);
        node.setAttribute('data-complete', '0');
        node.setAttribute('tabindex', 0);
        node.style.visibility = 'visible';
      });
    },

    resetFigureCardPlacHolderDefaultStates() {
      Array.from(document.getElementsByClassName('figure-placeholder')).forEach(node => {
        node.setAttribute('data-complete', '0');
        node.setAttribute('tabindex', -1);
      });
    },

    resetPreviouslySelectedFigureCardState(node, wasHighlighted) {
      node.classList.remove('highlight');
      node.setAttribute('aria-pressed', false);
      node.setAttribute('tabindex', wasHighlighted ? 0 : -1);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/sass/_zoom-reflow.scss';

%cardsContainer {
  display: inline-flex;
  justify-content: space-evenly;
  height: 45%;
  width: 100%;
  margin: 0;
  padding: 0;

  @supports (-ms-ime-align: auto) {
    & {
      justify-content: space-around !important;
    }
  }

  figcaption {
    color: black;
    font-family: hwtfutura;
    font-size: 16px;
    text-align: center;
    width: 100%;
  }
}

// CSS of the activity
section {
  .cards-container {
    @extend %cardsContainer;
    margin: 0px 0px 5% 0px;

    button {
      text-shadow: lighten(black, 50%) 0 0 20px;
      -webkit-box-shadow: 0px 0px 45px -4px rgba(0, 0, 0, 0.75);
      -moz-box-shadow: 0px 0px 45px -4px rgba(0, 0, 0, 0.75);
      box-shadow: 0px 0px 45px -4px rgba(0, 0, 0, 0.75);
      background: white;
      border: 0px;

      &.highlight {
        border: 2px solid red;
      }

      &.highlight-in-focus {
        border: none !important;
      }
    }
  }

  .puzzles-container {
    button {
      &.highlight {
        border: 2px solid red;
      }

      &.highlight-in-focus {
        border: none !important;
      }
    }

    @extend %cardsContainer;
    margin-top: -1.5%;
  }

  button {
    margin: 0;
    padding: 0;
  }

  .figure-type-3 {
    height: 95%;
    width: 22%;
  }

  .figure-type-4 {
    height: 88%;
    width: 20%;
  }

  .figure-type-5 {
    height: 90%;
    width: 17%;
  }

  @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: landscape) and (-webkit-min-device-pixel-ratio: 2) {
    .figure-type-3 {
      width: 20%;
    }

    .figure-type-4 {
      width: 18.5%;
    }
  }

  @include ifMaxWidth(690px) {
    %cardsContainer {
      figcaption {
        font-size: 8px;
      }
    }
  }
}
</style>
